import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { OptionKeyValue, Pathology, Relative } from '@myriadgenetics/mgh-types';
import { useHistory, useLocation } from '../../../helpers/routing';
import { OptionSelect } from '../../question';
import Steps from './selections';
import { isRelativeFemale, isRelativeMale } from './helper';
import { useButtonsWrapperContext } from '../../buttons-wrapper';
import { BackButton } from '../../buttons/back';
import Button from '../../../elements/button';

enum PathologySelectStep {
  Options = 'options',
  Other = 'other',
  Selected = 'selected',
}

interface PathologySelectState {
  curStep: PathologySelectStep;
  nextStep: PathologySelectStep;
  baseUrl: string;
  selectedValue: OptionKeyValue;
  selectedOtherValue: OptionKeyValue;
}

interface Props {
  isSelfFemale: boolean;
  isSelfMale: boolean;
  onPathologySelected: (selectedPathology: OptionKeyValue[]) => void;
  relative: Relative;
  selectionOptions?: Pathology[];
  chnState: any;
}

interface PathologySelectLocationState {
  state: {
    pathologySelectState?: PathologySelectState;
  };
  pathname: string;
}

function PathologySelectNode({ isSelfFemale, isSelfMale, onPathologySelected, relative, selectionOptions, chnState }: Props) {
  const { buttonsRef } = useButtonsWrapperContext();
  const history = useHistory();
  const { t } = useTranslation();
  const location: PathologySelectLocationState = useLocation() as PathologySelectLocationState;
  const { pathologySelectState } = location.state || {};
  const { curStep, nextStep, baseUrl } = pathologySelectState || {};

  const hasOtherPathology = pathologySelectState?.selectedValue?.optionValue === 'cancerHistory.cancers.other';
  const hasSelectedOtherPathology = pathologySelectState?.selectedOtherValue;
  const pathologySelectedStateIsDefined = chnState?.pathology != null || pathologySelectState?.selectedValue != null;

  const shouldShowContinue = () => {
    return hasSelectedOtherPathology || (curStep !== PathologySelectStep.Other && hasOtherPathology && pathologySelectedStateIsDefined);
  };

  useEffect(() => {
    if (!pathologySelectState) {
      history.replace(`${location.pathname}/select`, {
        ...location.state,
        pathologySelectState: {
          baseUrl: location.pathname,
          curStep: PathologySelectStep.Options,
        },
      });
    }
  });

  const triggerOnPathologySelected = (selectedPathology: OptionKeyValue[]) => {
    onPathologySelected(selectedPathology);
  };

  const navigateHistoryWithState = (routeMethod: 'replace' | 'push', val: OptionKeyValue[], curStep: PathologySelectStep, nextStep: PathologySelectStep) => {
    const stepMap: { [key: string]: string } = {
      options: 'options',
      other: 'other',
      selected: 'selected',
    };

    const expectedStep = routeMethod === 'replace' ? curStep : nextStep;
    const mappedStep = stepMap[expectedStep];
    const url = `${baseUrl}/${expectedStep}`;

    const state = {
      ...location.state,
      chnState: {
        ...chnState,
        pathology: null,
      },
      pathologySelectState: {
        ...pathologySelectState,
        selectedValue: val[0],
        ...(curStep === 'other' && { selectedOtherValue: val[0] }),
        curStep: mappedStep,
        nextStep,
      },
    };

    return history[routeMethod](url, state);
  };

  const handleOnPathologySelected = () => {
    const pathologySelected = chnState?.pathology;
    const otherPathologySelected = pathologySelectState?.selectedOtherValue;
    const goToOtherStep = nextStep === PathologySelectStep.Other;

    if (goToOtherStep && otherPathologySelected) {
      triggerOnPathologySelected([otherPathologySelected]);
      return;
    }

    if (goToOtherStep && !pathologySelected && !otherPathologySelected) {
      navigateHistoryWithState('push', [], PathologySelectStep.Options, PathologySelectStep.Other);
      return;
    }

    if (pathologySelected) {
      triggerOnPathologySelected([pathologySelected]);
      return;
    }
  };

  let internal;
  switch (curStep) {
    case PathologySelectStep.Options:
      const includeFemale = !isRelativeMale(relative, isSelfMale);
      const includeMale = !isRelativeFemale(relative, isSelfFemale);
      internal = (
        <OptionSelect
          compDef={Steps.pathologySelect(selectionOptions, includeFemale, includeMale, relative)}
          onResponseUpdated={(optionAnswerKey, val) => {
            const selectedPathology: string = val[0].optionValue;
            if (selectedPathology === 'cancerHistory.cancers.other') {
              navigateHistoryWithState('replace', val, PathologySelectStep.Options, PathologySelectStep.Other);
              navigateHistoryWithState('push', val, PathologySelectStep.Options, PathologySelectStep.Other);
            } else {
              navigateHistoryWithState('replace', val, PathologySelectStep.Options, PathologySelectStep.Options);
              triggerOnPathologySelected(val);
            }
          }}
          responses={
            chnState?.pathology !== null
              ? { cancer: [chnState?.pathology] }
              : { cancer: pathologySelectState?.selectedValue ? [pathologySelectState.selectedValue] : undefined }
          }
        />
      );
      break;
    case PathologySelectStep.Other:
      internal = (
        <OptionSelect
          compDef={Steps.pathologyOtherSelect}
          onResponseUpdated={(optionAnswerKey, val) => {
            if (pathologySelectState) {
              pathologySelectState.selectedOtherValue = val[0];
            }
            navigateHistoryWithState('replace', val, PathologySelectStep.Other, PathologySelectStep.Other);
            triggerOnPathologySelected(val);
          }}
          responses={{ pathology: pathologySelectState?.selectedOtherValue ? [pathologySelectState.selectedOtherValue] : undefined }}
        />
      );
      break;
    default:
      internal = <p>Initializing...</p>;
  }

  return (
    <div className="pathology-select">
      <div>{internal}</div>
      {buttonsRef?.current &&
        createPortal(
          <>
            <BackButton />
            {shouldShowContinue() && (
              <Button
                disabled={!pathologySelectedStateIsDefined}
                className="pathology-select__continue-button"
                dataTestId="continue-button"
                onClick={() => handleOnPathologySelected()}
                size="small"
              >
                {t('continue')}
              </Button>
            )}
            <BackButton />
          </>,
          buttonsRef.current,
        )}
    </div>
  );
}

export default PathologySelectNode;
